/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */
import {
	IDragAndDropRegistry,
	IDragAndDropRegistryEntries,
	IDraggableRegistryEntry,
	IDragMoveEventData,
	IDroppableRegistryEntry,
	TDraggableID,
	TDroppableID
} from "./DragAndDropTypes";
import { useEffect, useMemo } from "react";
import { draggable } from "./dragAndDropAttributes";

function createRegistry(): IDragAndDropRegistry {
	const entries: IDragAndDropRegistryEntries = {
		draggables: {},
		droppables: {}
	};

	const emitDragMove = (data: IDragMoveEventData) => {
		const draggables = Object.values(entries.draggables);
		const droppables = Object.values(entries.droppables);

		const allEntries = [ ...draggables, ...droppables ];

		for (let i = 0; i < allEntries.length; i++) {
			allEntries[i].onDragMove(data);
		}
	};

	const emitDrop = () => {
		const draggables = Object.values(entries.draggables);
		const droppables = Object.values(entries.droppables);

		const allEntries = [ ...draggables, ...droppables ];

		for (let i = 0; i < allEntries.length; i++) {
			allEntries[i].onDragDrop();
		}
	};

	const getDraggablesOfDroppable = (droppableId: TDroppableID): IDraggableRegistryEntry[] => {
		return Object.values(entries.draggables)
			.filter((draggable) => draggable.droppableId === droppableId)
			.sort((a, b) => a.index - b.index);
	};

	const findDraggableById = (id: TDraggableID): IDraggableRegistryEntry | null => {
		return entries.draggables[id] || null;
	};

	const draggableApi: IDragAndDropRegistry["draggable"] = {
		register: (entry) => {
			entries.draggables[entry.id] = entry;
		},
		unregister: (draggableId) => {
			const current = findDraggableById(draggableId);

			if (!current) {
				return;
			}

			delete entries.draggables[draggableId];
		},
		findById: findDraggableById,
		exists: (id) => !!findDraggableById(id)
	};

	const findDroppableById = (id: TDroppableID): IDroppableRegistryEntry | null => {
		return entries.droppables[id] || null;
	};

	const droppableApi: IDragAndDropRegistry["droppable"] = {
		register: (entry) => {
			entries.droppables[entry.id] = entry;
		},
		unregister: (droppableId) => {
			const current = findDroppableById(droppableId);

			if (!current) {
				return;
			}

			delete entries.droppables[droppableId];
		},
		findById: findDroppableById,
		exists: (id) => !!findDroppableById(id)
	};

	const clean: IDragAndDropRegistry["clean"] = () => {
		entries.draggables = {};
		entries.droppables = {};
	};

	return {
		emitDragMove,
		emitDrop,
		getDraggablesOfDroppable,
		draggable: draggableApi,
		droppable: droppableApi,
		clean
	};
}

export function useDragAndDropRegistry(): IDragAndDropRegistry {
	const registry = useMemo<IDragAndDropRegistry>(createRegistry, []);

	useEffect(() => {
		return () => {
			// clean up the registry to avoid any leaks
			// doing it after an animation frame so that other things unmounting
			// can continue to interact with the registry
			requestAnimationFrame(registry.clean);
		};
	}, [ registry ]);

	return registry;
}
